import React, {useMemo} from 'react';
import DashboardLayout from "../../../layouts/DashboardLayout";
import PageHeader from "../../../components/PageHeader";
import {useParams, Link} from 'react-router-dom';
import {useMutation, useQuery} from "react-query";
import StatusBadge from "../../../components/StatusBadge";
import Loading from "../../../components/Loading";
import {apollo} from "../../../utils/apollo";
import {GET_SETTLEMENT, GET_SETTLEMENT_REPORT_URL} from "../../../utils/graphql";
import Button from "../../../components/Button";
import InformationLine from "../../../components/InformationLine";
import MoneyHighlight from "../../../components/MoneyHighlight";
import {useTranslation} from "react-i18next";
import {CreditCardIcon, ArrowLeftIcon} from "@heroicons/react/24/outline";

function SettlementView() {
    const {settlement_uuid} = useParams();
    const {t} = useTranslation();

    const getSettlementReportUrlMutation = useMutation({
        mutationFn: async (variables) => {
            const {data} = await apollo.mutate({
                mutation: GET_SETTLEMENT_REPORT_URL,
                variables: {
                    settlementUuid: variables?.settlementUuid
                },
            });
            if (!!data?.getSettlementReportUrl) {
                window.location.href = data?.getSettlementReportUrl;
            }
        }
    })
    const fetchSettlement = useQuery(
        ['settlement', {settlement_uuid}],
        async () => {
            const {data} = await apollo.query({
                query: GET_SETTLEMENT,
                variables: {
                    settlement_uuid,
                },
            });
            return data?.getSettlement;
        },
        {keepPreviousData: false}
    );
    const settlement = useMemo(() => fetchSettlement?.isSuccess ? fetchSettlement?.data : null, [fetchSettlement]);

    return (
        <DashboardLayout>
            <div>
                <div className="mb-6">
                    <div className="flex items-center gap-3 mb-2">
                        <Link to="/merchant/settlements" className="p-2 rounded-lg bg-gray-100 hover:bg-gray-200 transition-colors">
                            <ArrowLeftIcon className="h-5 w-5 text-gray-600" />
                        </Link>
                        <div className="p-2 rounded-lg bg-gradient-to-br from-sky-500 to-teal-500 shadow-sm">
                            <CreditCardIcon className="h-6 w-6 text-white" />
                        </div>
                        <h2 className="text-2xl font-bold text-gray-900">{t('settlement.view_title') || 'Settlement Details'}</h2>
                    </div>
                    <p className="text-gray-600 mt-1">{t('settlement.view_description') || 'View detailed information about this settlement.'}</p>
                </div>

                {fetchSettlement.isLoading && (
                    <div className="bg-white rounded-xl shadow-md border border-gray-200 p-12 text-center">
                        <div className="flex justify-center items-center">
                            <div className="relative">
                                <div className="absolute inset-0 flex items-center justify-center">
                                    <div className="h-16 w-16 rounded-full bg-gradient-to-br from-sky-500 to-teal-500 opacity-20 animate-ping"></div>
                                </div>
                                <div className="relative flex items-center justify-center">
                                    <div className="h-16 w-16 rounded-full bg-gradient-to-br from-sky-500 to-teal-500 flex items-center justify-center">
                                        <CreditCardIcon className="h-8 w-8 text-white" />
                                    </div>
                                </div>
                            </div>
                        </div>
                        <h3 className="mt-6 text-lg font-semibold text-gray-900">{t('settlement.loading_title') || 'Loading Settlement'}</h3>
                        <p className="mt-2 text-sm text-gray-500">{t('settlement.loading_description') || 'Please wait while we fetch the settlement details...'}</p>
                    </div>
                )}

                {(fetchSettlement.isSuccess && settlement) && (
                    <div className="bg-white rounded-xl shadow-md border border-gray-200 overflow-hidden">
                        <div className="px-6 py-4 bg-gradient-to-br from-sky-500 to-teal-500 border-b border-gray-200 rounded-t-xl">
                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                    <div className="p-2 rounded-lg bg-white/20">
                                        <CreditCardIcon className="h-5 w-5 text-white" />
                                    </div>
                                    <h3 className="text-lg font-medium text-white">{t('settlement.settlement_details') || 'Settlement Details'}</h3>
                                </div>
                                <StatusBadge status={settlement?.status} className="bg-white/20 text-white border-white/30" />
                            </div>
                        </div>
                        <div className="p-6">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                                    <h4 className="text-sm font-semibold text-gray-500 uppercase mb-4">{t('settlement.view_sections.basic_information')}</h4>
                                    <div className="space-y-4">
                                        <div>
                                            <p className="text-xs font-medium text-gray-500">{t('settlement.view_sections.settlement_uuid')}</p>
                                            <p className="text-sm font-medium text-gray-900 mt-1">{settlement?.settlementUuid}</p>
                                        </div>
                                        <div>
                                            <p className="text-xs font-medium text-gray-500">{t('settlement.view_sections.merchant')}</p>
                                            <p className="text-sm font-medium text-gray-900 mt-1">{settlement?.merchant?.merchantName}</p>
                                        </div>
                                        <div>
                                            <p className="text-xs font-medium text-gray-500">{t('settlement.view_sections.supplier')}</p>
                                            <p className="text-sm font-medium text-gray-900 mt-1">{settlement?.supplier?.supplierName || 'N/A'}</p>
                                        </div>
                                        <div>
                                            <p className="text-xs font-medium text-gray-500">{t('settlement.view_sections.user')}</p>
                                            <p className="text-sm font-medium text-gray-900 mt-1">{settlement?.user?.name}</p>
                                        </div>
                                        <div>
                                            <p className="text-xs font-medium text-gray-500">{t('settlement.view_sections.authority')}</p>
                                            <p className="text-sm font-medium text-gray-900 mt-1">{settlement?.authority?.name || 'N/A'}</p>
                                        </div>
                                    </div>
                                </div>

                                <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                                    <h4 className="text-sm font-semibold text-gray-500 uppercase mb-4">{t('settlement.view_sections.financial_details')}</h4>
                                    <div className="space-y-4">
                                        <div>
                                            <p className="text-xs font-medium text-gray-500">{t('settlement.view_sections.request_amount')}</p>
                                            <div className="mt-1">
                                                <MoneyHighlight value={settlement?.requestAmount} currency={settlement?.currency?.symbol} className="text-blue-600 font-bold" />
                                            </div>
                                        </div>
                                        <div>
                                            <p className="text-xs font-medium text-gray-500">{t('settlement.view_sections.settlement_amount')}</p>
                                            <div className="mt-1">
                                                {!!settlement?.settlementAmount ? (
                                                    <MoneyHighlight value={settlement?.settlementAmount} currency={settlement?.currency?.symbol} className="text-green-600 font-bold text-xl" />
                                                ) : (
                                                    <span className="text-sm text-gray-500">N/A</span>
                                                )}
                                            </div>
                                        </div>
                                        <div>
                                            <p className="text-xs font-medium text-gray-500">{t('settlement.view_sections.settlement_date')}</p>
                                            <p className="text-sm font-medium text-gray-900 mt-1">{settlement?.settlementDate || 'N/A'}</p>
                                        </div>
                                        <div>
                                            <p className="text-xs font-medium text-gray-500">{t('settlement.view_sections.created_at')}</p>
                                            <p className="text-sm font-medium text-gray-900 mt-1">{settlement?.createdAt}</p>
                                        </div>
                                    </div>
                                </div>

                                <div className="bg-gray-50 p-4 rounded-lg border border-gray-200 md:col-span-2">
                                    <h4 className="text-sm font-semibold text-gray-500 uppercase mb-4">{t('settlement.view_sections.additional_information')}</h4>
                                    <div className="space-y-4">
                                        <div>
                                            <p className="text-xs font-medium text-gray-500">{t('settlement.view_sections.payment_reference')}</p>
                                            <p className="text-sm font-medium text-gray-900 mt-1">{settlement?.paymentReference || 'N/A'}</p>
                                        </div>
                                        <div>
                                            <p className="text-xs font-medium text-gray-500">{t('settlement.view_sections.remarks')}</p>
                                            <p className="text-sm font-medium text-gray-900 mt-1">{settlement?.remarks || 'N/A'}</p>
                                        </div>
                                    </div>
                                </div>

                                <div className="bg-gray-50 p-4 rounded-lg border border-gray-200 md:col-span-2">
                                    <h4 className="text-sm font-semibold text-gray-500 uppercase mb-4">{t('settlement.view_sections.documents')}</h4>
                                    <div className="flex flex-wrap gap-4">
                                        <Button 
                                            onClick={() => {
                                                window.location.href = `${process.env.REACT_APP_PAYD_API_BASE_URL}/${settlement?.receipt}`;
                                            }} 
                                            disabled={!settlement?.receipt}
                                        >
                                            {t('settlement.download_receipt') || 'Download Receipt'}
                                        </Button>

                                        <Button 
                                            onClick={() => {
                                                getSettlementReportUrlMutation.mutate({settlementUuid: settlement?.settlementUuid})
                                            }} 
                                            disabled={getSettlementReportUrlMutation.isLoading}
                                        >
                                            {t('settlement.download_report') || 'Download Report'}
                                        </Button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </DashboardLayout>
    );
}

export default SettlementView;
